// Chemin: C:\gestion-copro\src\components\layout\AdminLayout.tsx
// Layout pour les pages d'administration COPRO PROTECT
// Session 34 : ajout lien Messagerie
// Session 41 : ajout lien Courriers
// Session 59 : Refonte header - Logo Copro Protect + Sceau copropriete + Nom
// Session 60 : Ajout lien Admin SaaS pour super admin dans le header
// Session 67 : Ajout lien Comptabilite (module verification comptable)
// Session 70 : Ajout badges alertes sur menu lateral (actions en attente)
// Session 74 : Ajout bouton "Mon espace copropriétaire" pour membres CS
// Session 90 : Remplacement mapping rôles en dur par USER_ROLE_LABELS (source unique)
// Session 93 : Masquer "Voir le formulaire public" pour Admin SaaS
// Session 94 : Ajout lien Calendrier dans la sidebar
// Session 213 : Harmonisation sidebar — icônes Lucide, groupes visuels, item actif bg-blue-900,
//               export données RGPD, dropdown modernisé, overlay backdrop-blur

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Shield, ChevronDown, LogOut, Settings, Home,
  LayoutDashboard, Building2, SquareStack, Users, UsersRound,
  BookUser, FolderOpen, CalendarClock, Calculator,
  FileText, AlertTriangle, Mail, Inbox, Download, Sparkles
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useAlertes } from '../../hooks/useAlertes';
import { USER_ROLE_LABELS, UserRole } from '../../types/user.types';

// ============================================
// TYPES
// ============================================

interface AdminLayoutProps {
  children: React.ReactNode;
}

type AlerteKey = 'courriers' | 'messagerie' | 'comptabilite' | 'signalements' | 'affaires';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  alerteKey?: AlerteKey;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

// ============================================
// NAVIGATION — Groupes avec icônes Lucide
// ============================================

const iconClass = 'w-[18px] h-[18px]';

const navGroups: NavGroup[] = [
  {
    // Groupe principal — Vue d'ensemble et gestion
    items: [
      { path: '/admin',                   label: 'Tableau de bord',   icon: <LayoutDashboard className={iconClass} /> },
      { path: '/admin/copropriete',        label: 'Copropriété',       icon: <Building2 className={iconClass} /> },
      { path: '/admin/lots',               label: 'Lots',              icon: <SquareStack className={iconClass} /> },
      { path: '/admin/coproprietaires',    label: 'Copropriétaires',   icon: <Users className={iconClass} /> },
      { path: '/admin/conseil-syndical',   label: 'Conseil Syndical',  icon: <UsersRound className={iconClass} /> },
      { path: '/admin/interlocuteurs',     label: 'Interlocuteurs',    icon: <BookUser className={iconClass} /> },
    ],
  },
  {
    // Groupe opérationnel — Activité courante
    label: 'Activité',
    items: [
      { path: '/admin/affaires',      label: 'Affaires',       icon: <FolderOpen className={iconClass} />,  alerteKey: 'affaires' },
      { path: '/admin/calendrier',    label: 'Calendrier',     icon: <CalendarClock className={iconClass} /> },
      { path: '/admin/comptabilite',  label: 'Comptabilité',   icon: <Calculator className={iconClass} />,  alerteKey: 'comptabilite' },
      { path: '/admin/documents',     label: 'Documents',      icon: <FileText className={iconClass} /> },
      { path: '/admin/signalements',  label: 'Signalements',   icon: <AlertTriangle className={iconClass} />, alerteKey: 'signalements' },
      { path: '/admin/messagerie',    label: 'Messagerie',     icon: <Mail className={iconClass} />,        alerteKey: 'messagerie' },
      { path: '/admin/courriers',     label: 'Courriers',      icon: <Inbox className={iconClass} />,       alerteKey: 'courriers' },
    ],
  },
  {
    // Groupe données personnelles
    label: 'Mes données',
    items: [
      { path: '/admin/export-donnees', label: 'Exporter mes données', icon: <Download className={iconClass} /> },
    ],
  },
];

// ============================================
// URL LOGO COPRO PROTECT
// ============================================

const LOGO_COPRO_PROTECT_URL = '/logo-copro-protect.png';

// ============================================
// COMPOSANT BADGE ALERTE
// ============================================

interface BadgeAlerteProps {
  count: number;
  type?: 'danger' | 'warning';
}

const BadgeAlerte: React.FC<BadgeAlerteProps> = ({ count, type = 'danger' }) => {
  if (count === 0) return null;
  const bgColor = type === 'danger' ? 'bg-red-500' : 'bg-amber-500';
  return (
    <span className={`ml-auto min-w-[20px] h-5 px-1.5 ${bgColor} text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse`}>
      {count > 99 ? '99+' : count}
    </span>
  );
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { userProfile, signOut } = useAuth();
  const { copropriete, loading: coproprieteLoading } = useCopropriete();
  const { alertes } = useAlertes();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdminSaas = userProfile?.role === 'admin_saas' || userProfile?.isGestionnaireSaas === true;
  const isMembreCS = userProfile?.role === 'cs';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const userInitial = userProfile?.prenom?.charAt(0)?.toUpperCase() ||
                      userProfile?.email?.charAt(0)?.toUpperCase() || 'U';

  const displayName = userProfile?.prenom ||
                      userProfile?.email?.split('@')[0] || 'Utilisateur';

  const getAlerteCount = (alerteKey?: AlerteKey): number => {
    if (!alerteKey) return 0;
    return alertes[alerteKey] || 0;
  };

  const getBadgeType = (alerteKey?: AlerteKey): 'danger' | 'warning' =>
    alerteKey === 'affaires' ? 'warning' : 'danger';

  const isItemActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-2">

          {/* ZONE GAUCHE */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-blue-800 transition-colors relative"
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              {alertes.total > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {alertes.total > 9 ? '9+' : alertes.total}
                </span>
              )}
            </button>
            <Link to="/admin" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
              <img
                src={LOGO_COPRO_PROTECT_URL}
                alt="Copro Protect"
                className="h-10 w-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div className="hidden items-center justify-center w-10 h-10 bg-blue-700 rounded-lg">
                <Shield className="w-6 h-6" />
              </div>
              <span className="font-bold text-lg hidden md:inline">Copro Protect</span>
            </Link>
          </div>

          {/* ZONE CENTRE */}
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-blue-400">•</span>
            <span className="font-semibold text-sm sm:text-base truncate max-w-[200px] sm:max-w-[300px]">
              {coproprieteLoading ? (
                <span className="text-blue-300">Chargement...</span>
              ) : (
                copropriete?.nom || 'Copropriété'
              )}
            </span>
            <span className="px-2.5 py-0.5 bg-blue-700 text-blue-100 text-xs font-bold rounded-full tracking-wide">
              CS
            </span>
          </div>

          {/* ZONE DROITE */}
          <div className="flex items-center gap-3">
            {isAdminSaas && (
              <Link
                to="/admin-saas"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                Admin SaaS
              </Link>
            )}
            {isMembreCS && (
              <Link
                to="/espace-copro"
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                Mon espace
              </Link>
            )}

            {/* Profil */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-800 transition-colors"
              >
                <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center font-semibold text-sm border-2 border-blue-400">
                  {userInitial}
                </span>
                <span className="hidden sm:inline text-sm font-medium">{displayName}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                    <div className="p-4 border-b border-gray-100 bg-gradient-to-br from-blue-50 to-gray-50">
                      <p className="font-semibold text-gray-800">{userProfile?.prenom} {userProfile?.nom}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{userProfile?.email}</p>
                      {userProfile?.role && (
                        <span className="inline-block mt-2 px-2.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                          {userProfile.isGestionnaireSaas ? 'Admin SaaS' :
                           USER_ROLE_LABELS[userProfile.role as UserRole] || userProfile.role}
                        </span>
                      )}
                    </div>
                    <div className="p-2">
                      {isAdminSaas && (
                        <Link
                          to="/admin-saas"
                          onClick={() => setDropdownOpen(false)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm sm:hidden"
                        >
                          <Settings className="w-4 h-4" />
                          Admin SaaS
                        </Link>
                      )}
                      {isMembreCS && (
                        <Link
                          to="/espace-copro"
                          onClick={() => setDropdownOpen(false)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors text-sm font-medium"
                        >
                          <Home className="w-4 h-4" />
                          Mon espace copropriétaire
                        </Link>
                      )}
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                      >
                        <LogOut className="w-4 h-4" />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-[60px] left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <nav className="flex flex-col h-full overflow-y-auto">
          <div className="flex-1 py-3">
            {/* Bouton SENTINELLE — accès prioritaire, en tête de sidebar */}
            <div className="px-3 pb-3">
              <Link
                to="/admin/sentinelle"
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 w-full
                  ${location.pathname === '/admin/sentinelle'
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 hover:border-amber-300'
                  }
                `}
              >
                <span className={`
                  flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0
                  ${location.pathname === '/admin/sentinelle' ? 'bg-white/20' : 'bg-amber-100'}
                `}>
                  <Sparkles className="w-[18px] h-[18px]" />
                </span>
                <span className="text-sm font-semibold flex-1">SENTINELLE</span>
                {location.pathname === '/admin/sentinelle' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-white flex-shrink-0" />
                )}
              </Link>
            </div>

            {navGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                {group.label && (
                  <div className="px-4 pt-5 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                        {group.label}
                      </span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                  </div>
                )}
                <div className="px-3 space-y-0.5">
                  {group.items.map((item) => {
                    const isActive = isItemActive(item.path);
                    const alerteCount = getAlerteCount(item.alerteKey);
                    const badgeType = getBadgeType(item.alerteKey);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150
                          ${isActive
                            ? 'bg-blue-900 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        <span className={`
                          flex items-center justify-center w-8 h-8 rounded-lg transition-colors flex-shrink-0
                          ${isActive
                            ? 'bg-white/10'
                            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700'
                          }
                        `}>
                          {item.icon}
                        </span>
                        <span className={`text-sm flex-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
                          {item.label}
                        </span>
                        {isActive && (
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                        )}
                        <BadgeAlerte count={alerteCount} type={badgeType} />
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Formulaire public — masqué pour Admin SaaS */}
          {!isAdminSaas && (
            <div className="p-3 border-t border-gray-100">
              <a
                href="/signaler"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 p-3 bg-gray-50 rounded-xl text-gray-500 hover:bg-gray-100 transition-colors text-sm border border-gray-200"
              >
                Voir le formulaire public
              </a>
            </div>
          )}
        </nav>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenu principal */}
      <main className="lg:ml-64 pt-[60px] min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
